import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, RefreshCcw, ShieldCheck, Users, Building2, Clock } from "lucide-react";
import { toast } from "sonner";

const STATUS_ORDER = ["aguardando_limpeza", "em_limpeza", "aguardando_liberacao", "manutencao"];

const fmtDT = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function AdminLimpeza() {
  const [quartos, setQuartos] = useState([]);
  const [ocorrencias, setOcorrencias] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [pousadaFilter, setPousadaFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cleaning/quartos");
      const relevant = (data || []).filter((q) => STATUS_ORDER.includes(q.status));
      setQuartos(data || []);
      // buscar ocorrências recentes para cada quarto relevante em paralelo
      const results = await Promise.all(
        relevant.map((q) => api.get(`/cleaning/quartos/${q.id}`).then((r) => [q.id, r.data]).catch(() => [q.id, null])),
      );
      const map = {};
      results.forEach(([id, d]) => { if (d) map[id] = d.ocorrencia || {}; });
      setOcorrencias(map);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar limpeza.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const pousadas = useMemo(() => {
    const map = new Map();
    quartos.forEach((q) => map.set(q.pousada_id, q.pousada_nome));
    return Array.from(map, ([id, nome]) => ({ id, nome })).sort((a, b) => a.nome.localeCompare(b.nome));
  }, [quartos]);

  const filtrados = useMemo(() => {
    const rank = { aguardando_limpeza: 0, em_limpeza: 1, aguardando_liberacao: 2, manutencao: 3 };
    return quartos
      .filter((q) => STATUS_ORDER.includes(q.status))
      .filter((q) => pousadaFilter === "all" || q.pousada_id === pousadaFilter)
      .sort((a, b) => (rank[a.status] ?? 9) - (rank[b.status] ?? 9)
        || String(a.numero).localeCompare(String(b.numero), "pt-BR", { numeric: true }));
  }, [quartos, pousadaFilter]);

  const contadores = useMemo(() => {
    const c = { aguardando_limpeza: 0, em_limpeza: 0, aguardando_liberacao: 0, manutencao: 0 };
    filtrados.forEach((q) => { c[q.status] = (c[q.status] || 0) + 1; });
    return c;
  }, [filtrados]);

  const liberarAdmin = async (quartoId) => {
    if (!window.confirm("Confirmar liberação deste quarto? Ele voltará a estar disponível para reservas.")) return;
    setBusyId(quartoId);
    try {
      await api.post(`/admin/quartos/${quartoId}/liberar`);
      toast.success("Quarto liberado");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao liberar quarto.");
    } finally { setBusyId(null); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Operação · Limpeza"
        title="Painel de Limpeza (Admin)"
        description="Acompanhe todos os quartos que precisam de atenção e libere manualmente quando necessário."
        actions={
          <div className="flex items-center gap-2">
            <Link to="/admin/usuarios-limpeza">
              <Button variant="outline" data-testid="btn-goto-usuarios-limpeza" className="text-sm">
                <Users size={14} className="mr-1.5" /> Colaboradoras
              </Button>
            </Link>
            <Button onClick={load} variant="outline" data-testid="btn-admin-limpeza-refresh" className="text-sm">
              <RefreshCcw size={14} className="mr-1.5" /> Atualizar
            </Button>
          </div>
        }
      />

      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatBox testid="stat-aguardando" label="Aguardando limpeza" value={contadores.aguardando_limpeza} color="text-[#B4472B] bg-[#FEE7DE]" />
        <StatBox testid="stat-em-limpeza" label="Em limpeza" value={contadores.em_limpeza} color="text-[#1E4C7A] bg-[#DBE7F2]" />
        <StatBox testid="stat-aguardando-liberacao" label="Aguardando liberação" value={contadores.aguardando_liberacao} color="text-[#5E3B7A] bg-[#E9DFEF]" />
        <StatBox testid="stat-manutencao" label="Em manutenção" value={contadores.manutencao} color="text-[#8B6A1F] bg-[#FBEDD1]" />
      </section>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select value={pousadaFilter} onValueChange={setPousadaFilter}>
          <SelectTrigger className="w-64 bg-white" data-testid="filter-admin-pousada">
            <SelectValue placeholder="Todas as pousadas" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as pousadas</SelectItem>
            {pousadas.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-stone-500">{filtrados.length} quartos exibidos</span>
      </div>

      {loading ? (
        <div className="text-stone-500 text-sm py-12 text-center" data-testid="admin-limpeza-loading">Carregando…</div>
      ) : filtrados.length === 0 ? (
        <div className="bg-[#FFFFFF] border border-dashed border-stone-300 rounded-lg p-12 text-center">
          <Sparkles size={40} className="mx-auto text-stone-300 mb-3" strokeWidth={1.4} />
          <div className="text-stone-500 text-sm">Nenhum quarto precisa de atenção no momento.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="admin-limpeza-list">
          {filtrados.map((q) => {
            const oc = ocorrencias[q.id] || {};
            return (
              <div key={q.id} data-testid={`admin-limpeza-card-${q.id}`}
                className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-5">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-400 flex items-center gap-1 mb-1">
                      <Building2 size={11} /> {q.pousada_nome}
                    </div>
                    <div className="font-serif text-2xl text-stone-900 leading-none">Quarto {q.numero}</div>
                    <div className="text-xs text-stone-500 mt-1 capitalize">
                      {q.tipo || "—"} · até {q.capacidade || 1} pax
                    </div>
                  </div>
                  <StatusPill status={q.status} />
                </div>

                <ul className="text-xs space-y-1.5 mb-3">
                  <TimelineItem label="Check-out" data={oc.checkout_em} />
                  <TimelineItem label="Iniciou limpeza" data={oc.iniciou_em} por={oc.iniciou_por} />
                  <TimelineItem label="Finalizou limpeza" data={oc.finalizou_em} por={oc.finalizou_por} />
                  <TimelineItem label="Liberou quarto" data={oc.liberou_em} por={oc.liberou_por} />
                </ul>

                <div className="flex items-center gap-2 pt-3 border-t border-stone-100">
                  <Button
                    onClick={() => liberarAdmin(q.id)}
                    disabled={busyId === q.id || q.status === "manutencao"}
                    data-testid={`btn-admin-liberar-${q.id}`}
                    className="bg-[#083A6B] hover:bg-[#052B50] text-white text-xs h-9"
                  >
                    <ShieldCheck size={14} className="mr-1.5" /> Liberar quarto
                  </Button>
                  {q.status === "manutencao" && (
                    <span className="text-[11px] text-[#8B6A1F]">Retire do modo manutenção em /quartos primeiro.</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, color, testid }) {
  return (
    <div data-testid={testid} className={`rounded-lg p-5 ${color}`}>
      <div className="text-[10px] uppercase tracking-widest font-semibold opacity-90">{label}</div>
      <div className="font-serif text-4xl leading-none mt-2">{value}</div>
    </div>
  );
}

function TimelineItem({ label, data, por }) {
  return (
    <li className="flex items-start justify-between gap-2">
      <span className="text-stone-500 flex items-center gap-1"><Clock size={10} /> {label}</span>
      <span className="text-right">
        <span className="text-stone-800">{data ? fmtDT(data) : <span className="text-stone-400">—</span>}</span>
        {por && <span className="text-[10px] text-stone-400 block">{por}</span>}
      </span>
    </li>
  );
}
